import type { TFunction } from 'i18next'

export type GuidelineValidationError =
  | 'EMPTY_INPUT'
  | 'INVALID_NUMBER'
  | 'OUT_OF_RANGE'
  | 'NOT_FINITE'
  | 'DUPLICATE_GUIDE'
  | 'NO_PRODUCT_WIDTH'

type ValidationResult =
  | { isValid: true; percentage: number }
  | { isValid: false; error: GuidelineValidationError }

/**
 * Validates percentage input for guideline creation
 * Handles empty input, invalid numbers, range checking, and finite validation
 */
export const validateGuidelinePercentage = (
  input: string,
): ValidationResult => {
  // Trim whitespace from input
  const trimmedInput = input.trim()

  // Check for empty input
  if (!trimmedInput) {
    return { isValid: false, error: 'EMPTY_INPUT' }
  }

  // Parse the percentage value (allows decimals like 25.71)
  const percentage = parseFloat(trimmedInput)

  // Validate the parsed number
  if (isNaN(percentage)) {
    return { isValid: false, error: 'INVALID_NUMBER' }
  }

  // Validate it's a finite number (not Infinity or -Infinity)
  if (!isFinite(percentage)) {
    return { isValid: false, error: 'NOT_FINITE' }
  }

  // Validate range is within 0-100
  if (percentage < 0 || percentage > 100) {
    return { isValid: false, error: 'OUT_OF_RANGE' }
  }

  // All validation passed
  return { isValid: true, percentage }
}

/**
 * Gets the translated error message for a validation error
 */
export const getGuidelineErrorMessage = (
  error: GuidelineValidationError,
  t: TFunction,
): string => {
  const errorKeys: Record<GuidelineValidationError, string> = {
    EMPTY_INPUT:
      'editor.menus.properties.settings.snapToGuides.errors.emptyInput',
    INVALID_NUMBER:
      'editor.menus.properties.settings.snapToGuides.errors.invalidNumber',
    OUT_OF_RANGE:
      'editor.menus.properties.settings.snapToGuides.errors.outOfRange',
    NOT_FINITE:
      'editor.menus.properties.settings.snapToGuides.errors.notFinite',
    DUPLICATE_GUIDE:
      'editor.menus.properties.settings.snapToGuides.errors.duplicateGuide',
    NO_PRODUCT_WIDTH:
      'editor.menus.properties.settings.snapToGuides.errors.noProductWidth',
  }

  return t(errorKeys[error])
}
